// PhotoQueuePill (R114 fix — Stacy's app role is 'manager', so after reopening
// the app she lands on Analytics, and the only "photos still on this phone"
// count lived inside the inspector field view / a capture screen). One small
// app-level pill on whatever page anyone lands on, while inspection photos
// wait on THIS phone: "📤 N photos on this phone waiting to upload · Review" →
// the existing PhotoQueueSheet. Hidden when the page already shows its own
// count (the field view / office cockpit banner, the capture tally) or the
// sheet is open, and for owners / the accounting partner. Refreshed on the
// queue's own events ('surge-frame-queue'); reads the in-memory queue only.
// HostBuddy tokens only (styles/main.css :root).
// R114 fix2 (it sat on the inspection report's sticky Edit scoring / manual
// checklist / Complete inspection bar at 390px, and over modals): it also
// stays hidden on any screen that owns a bottom action bar (shared/lib/
// bottomBars — the report and capture bars, the Issues bulk bar, the Jarvis
// composer, a [data-bottom-bar]); the report shows the same count as a banner instead. It
// sits UNDER every overlay layer (modals 150+, popovers, the mobile nav drawer
// at 99/100), and re-checks the moment the page's DOM changes or something is
// tapped — not on the next 3 s tick.
import { counts, isSyncing } from '../../lib/captureQueue';
import { openPhotoQueueSheet, photoQueueSheetOpen } from './PhotoQueueSheet';
import { phoneQueueLine } from '../../../../lib/frameQueuePolicy.mjs';
import { bottomBarShown } from '../lib/bottomBars';

const PILL_ID = 'fq-pill';
const STYLE_ID = 'fq-pill-style';
let mounted = false;
let lastSig = '';

function injectStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const st = document.createElement('style');
  st.id = STYLE_ID;
  st.textContent = `
    #${PILL_ID} { position:fixed; right:16px; bottom:calc(16px + env(safe-area-inset-bottom, 0px)); z-index:95; max-width:calc(100vw - 32px); box-sizing:border-box; }
    #${PILL_ID} .fqp-btn { display:flex; align-items:center; gap:10px; width:100%; min-height:44px; padding:6px 6px 6px 14px; box-sizing:border-box;
      border-radius:var(--radius-pill); border:1px solid var(--accent-border); background:var(--card); color:var(--text);
      font:inherit; font-size:13px; font-weight:600; line-height:1.3; text-align:left; box-shadow:var(--shadow-pop); cursor:pointer; }
    #${PILL_ID} .fqp-btn:hover { border-color:var(--accent); }
    #${PILL_ID} .fqp-txt { flex:1 1 auto; min-width:0; }
    #${PILL_ID} .fqp-go { flex:none; display:inline-flex; align-items:center; min-height:32px; padding:0 14px; border-radius:var(--radius-pill); background:var(--accent); color:#fff; font-size:12px; font-weight:700; }
    @media (max-width:640px) { #${PILL_ID} { left:16px; right:16px; } }
  `;
  document.head.appendChild(st);
}

// on a phone the bottom-left corner holds the shared-login chip and the
// "office asked you" pill — sit above them instead of on top of them
function place(el: HTMLElement): void {
  let bottom = 16;
  if (window.innerWidth <= 640) {
    for (const id of ['shared-actor-chip', 'info-ask-pill']) {
      const o = document.getElementById(id);
      if (!o) continue;
      const r = o.getBoundingClientRect();
      if (r.height > 0) bottom = Math.max(bottom, Math.round(window.innerHeight - r.top + 8));
    }
  }
  el.style.bottom = `calc(${bottom}px + env(safe-area-inset-bottom, 0px))`;
}

function render(): void {
  const c = counts(null);
  const line = phoneQueueLine(c, isSyncing());
  // the page already says it (field view / office cockpit / report banner,
  // capture tally), the screen owns a bottom action bar, or the sheet is up
  const own = !!document.querySelector('#ifld-fq.ifld-fq, #cap-frames-tally') || bottomBarShown();
  const show = !!line && !own && !photoQueueSheetOpen();
  let el = document.getElementById(PILL_ID);
  if (!show) { el?.remove(); lastSig = ''; return; }
  if (!el) {
    injectStyle();
    el = document.createElement('div');
    el.id = PILL_ID;
    el.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement | null)?.closest?.('.fqp-btn')) return;
      openPhotoQueueSheet({});
      render();
    });
    document.body.appendChild(el);
    lastSig = '';
  }
  if (line !== lastSig) {
    el.innerHTML = `<button type="button" class="fqp-btn" aria-label="${line} — review"><span aria-hidden="true">📤</span><span class="fqp-txt">${line}</span><span class="fqp-go">Review</span></button>`;
    lastSig = line;
  }
  place(el);
}

export function mountPhotoQueuePill(): void {
  const role = (() => { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } })();
  if (role === 'owner' || role === 'accounting') { document.getElementById(PILL_ID)?.remove(); return; }
  render();
  if (mounted) return;
  mounted = true;
  window.addEventListener('surge-frame-queue', () => render());
  window.addEventListener('resize', () => { const el = document.getElementById(PILL_ID); if (el) place(el); });
  // pages redraw (a report bar or a banner appears), a tap opens a bar / the
  // info-ask list, the sheet closes — re-check on the next frame (coalesced;
  // the check is an in-memory count + two selector lookups)
  let raf = 0;
  const soon = () => { if (!raf) raf = window.requestAnimationFrame(() => { raf = 0; render(); }); };
  try { new MutationObserver(soon).observe(document.body, { childList: true, subtree: true }); } catch { /* the interval below still runs */ }
  document.addEventListener('click', soon);
  window.setInterval(() => { if (!document.hidden) render(); }, 3000);
}
